import { isPasswordAcceptable } from '@acasa/shared'
import { useState } from 'react'
import { Card } from '@/components/card'
import { TextInput } from '@/features/handovers/fields'
import { ApiRequestError } from '@/lib/api'
import { PasswordRules } from './password-rules'
import {
  useAddPasskey,
  useChangePassword,
  usePasskeys,
  useRemovePasskey,
  useSession,
} from './use-auth'

/** Managing your own passkeys — add a device, revoke one that is gone. */
export function PasskeyCard() {
  const passkeys = usePasskeys()
  const add = useAddPasskey()
  const remove = useRemovePasskey()
  const [error, setError] = useState<string | null>(null)

  const rows = passkeys.data?.passkeys ?? []

  return (
    <Card
      title="Passkeys (optional)"
      footer="Ein Passkey ersetzt Passwort und Code durch Face ID, Fingerabdruck oder die Geräte-PIN — nur auf dieser Adresse, und er lässt sich weder abfragen noch weitergeben. Ohne Passkey funktioniert die Anmeldung unverändert."
    >
      <ul className="mb-4 flex flex-col">
        {rows.map((passkey) => (
          <li
            key={passkey.id}
            className="border-line flex items-baseline justify-between gap-4 border-b py-2.5 last:border-0"
          >
            <span className="min-w-0">
              <span className="text-ink block truncate text-sm">{passkey.label ?? 'Passkey'}</span>
              <span className="text-faint text-[11px]">
                seit {new Date(passkey.createdAt).toLocaleDateString('de-DE')}
                {passkey.lastUsedAt
                  ? ` · zuletzt ${new Date(passkey.lastUsedAt).toLocaleDateString('de-DE')}`
                  : ' · noch nicht verwendet'}
                {passkey.backedUp ? ' · synchronisiert' : ''}
              </span>
            </span>

            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => {
                setError(null)
                remove.mutate(passkey.id, {
                  onError: (mutationError) => setError(message(mutationError)),
                })
              }}
              className="text-faint hover:text-danger shrink-0 text-[11px] underline underline-offset-4 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            >
              entfernen
            </button>
          </li>
        ))}

        {rows.length === 0 && !passkeys.isLoading && (
          <li className="text-muted py-2 text-sm">
            Noch kein Passkey hinterlegt — die Anmeldung läuft über Passwort und Code.
          </li>
        )}
      </ul>

      <button
        type="button"
        disabled={add.isPending}
        onClick={() => {
          setError(null)
          add.mutate(deviceLabel(), {
            onError: (mutationError) => setError(message(mutationError)),
          })
        }}
        className="bg-brand text-surface hover:bg-brand-strong px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
      >
        {add.isPending ? 'warte auf Bestätigung …' : 'Passkey für dieses Gerät anlegen'}
      </button>

      {error && <p className="text-danger mt-3 text-xs">{error}</p>}
    </Card>
  )
}

function deviceLabel(): string {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return 'Android-Gerät'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows-PC'
  return 'Gerät'
}

function message(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) {
    if (error.name === 'NotAllowedError') return 'Abgebrochen oder nichts ausgewählt.'
    if (error.name === 'InvalidStateError') {
      return 'Auf diesem Gerät ist bereits ein Passkey für dieses Konto vorhanden.'
    }
    return error.message
  }
  return 'Unbekannter Fehler'
}

/**
 * The fallback password.
 *
 * Its own card, below the passkeys, because that is the order of importance:
 * the passkey is how you get in, this is what you fall back to — and only
 * together with the code from the authenticator app.
 */
export function PasswordCard() {
  const { user } = useSession()
  const change = useChangePassword()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!user) return null

  const mismatch = repeat.length > 0 && next !== repeat

  return (
    <Card title="Ersatz-Zugang">
      <p className="text-muted mb-4 text-sm leading-relaxed">
        Passwort und Authenticator-Code zusammen sind der Weg hinein, wenn kein Gerät mit Passkey
        zur Hand ist. Einzeln genügt keines von beiden.
      </p>

      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          setDone(false)
          change.mutate(
            { currentPassword: current, password: next },
            {
              onSuccess: () => {
                setDone(true)
                setCurrent('')
                setNext('')
                setRepeat('')
              },
              onError: (mutationError) => setError(message(mutationError)),
            },
          )
        }}
      >
        <input type="email" value={user.email} autoComplete="username" readOnly hidden />

        <TextInput
          type="password"
          autoComplete="current-password"
          placeholder="Aktuelles Passwort"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          required
        />
        <TextInput
          type="password"
          autoComplete="new-password"
          placeholder="Neues Passwort"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          required
        />
        <TextInput
          type="password"
          autoComplete="new-password"
          placeholder="Neues Passwort wiederholen"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          required
        />

        {mismatch && (
          <p className="text-danger text-xs">Die beiden Eingaben stimmen nicht überein.</p>
        )}

        <PasswordRules password={next} subject={user} />

        <button
          type="submit"
          disabled={change.isPending || next !== repeat || !isPasswordAcceptable(next, user)}
          className="bg-brand text-surface hover:bg-brand-strong self-start px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
        >
          {change.isPending ? 'wird gespeichert …' : 'Passwort ändern'}
        </button>

        {error && <p className="text-danger text-xs">{error}</p>}
        {done && <p className="text-positive text-xs">Passwort geändert.</p>}
      </form>
    </Card>
  )
}
